"use client";

import { FiCopy } from "react-icons/fi";

import Button from "@/components/ui/button";
import Tooltip from "@/components/ui/tooltip";

import { cx } from "@/lib/tailwind-utils";
import { useToast } from "@/lib/use-toast";

export function MedicationNameTooltip({
  name,
  grow = true,
}: {
  name: string;
  /** Off when something sits right after the name, like a dilution badge. */
  grow?: boolean;
}) {
  const { toast } = useToast();

  const copyName = async () => {
    try {
      await navigator.clipboard.writeText(name);
      toast({
        title: "Copied",
        description: "Medication name copied to clipboard",
        variant: "success",
      });
    } catch {
      toast({
        title: "Unable to copy",
        description: "Please try again or select and copy the medication name.",
        variant: "error",
      });
    }
  };

  return (
    <Tooltip
      variant="light"
      triggerAsChild
      side="top"
      aria-label={name}
      showArrow={false}
      sideOffset={6}
      className="w-auto max-w-[min(24rem,calc(100vw-2rem))] select-text rounded-lg bg-background px-3 py-2 shadow-md"
      content={
        <div className="flex items-start gap-3">
          <p className="min-w-0 whitespace-normal break-words py-1 text-body leading-5 [overflow-wrap:anywhere]">
            {name}
          </p>
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            className="size-7 shrink-0 rounded-md text-foreground-muted hover:text-foreground"
            aria-label="Copy medication name"
            onMouseDown={(event) => event.preventDefault()}
            onClick={(event) => {
              event.stopPropagation();
              void copyName();
            }}
          >
            <FiCopy className="size-3.5" aria-hidden="true" />
          </Button>
        </div>
      }
    >
      <button
        type="button"
        className={cx(
          "min-w-0 cursor-pointer rounded text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary",
          grow && "flex-1",
        )}
        onClick={(event) => {
          event.stopPropagation();
          void copyName();
        }}
      >
        <span className="line-clamp-2 break-words">{name}</span>
      </button>
    </Tooltip>
  );
}
